import { object, func, bool, string } from "prop-types"
import { useNavigate, useParams } from "react-router-dom"
import { ExpressCheckoutElement } from "@stripe/react-stripe-js"
import { CenterLabelBorder } from "source/shared/components"
import { useEnv } from "source/shared/hooks/useEnv"
import { useElements } from "@stripe/react-stripe-js"
import { useReservationMutation } from "source/registrations/hooks/useReservation"

const ExpressCheckoutWrapper = ({
  stripe,
  paymentIntent,
  loading,
  onExpressCheckoutReady,
  canMakePayment,
}) => {
  const { eventId, reservationUuid, registrationId } = useParams()

  return registrationId ? (
    <ExpressCheckoutInConfirmationContext
      stripe={stripe}
      paymentIntent={paymentIntent}
      loading={loading}
      canMakePayment={canMakePayment}
      onExpressCheckoutReady={onExpressCheckoutReady}
      registrationId={registrationId}
    />
  ) : (
    <ExpressCheckoutInReservationContext
      stripe={stripe}
      paymentIntent={paymentIntent}
      loading={loading}
      canMakePayment={canMakePayment}
      onExpressCheckoutReady={onExpressCheckoutReady}
      reservationUuid={reservationUuid}
      eventId={eventId}
    />
  )
}

ExpressCheckoutWrapper.propTypes = {
  stripe: object.isRequired,
  paymentIntent: object.isRequired,
  loading: bool.isRequired,
  onExpressCheckoutReady: func.isRequired,
  canMakePayment: bool,
}

const ExpressCheckoutInConfirmationContext = ({
  stripe,
  paymentIntent,
  loading,
  onExpressCheckoutReady,
  registrationId,
  canMakePayment,
}) => {
  return (
    <ExpressCheckout
      stripe={stripe}
      paymentIntent={paymentIntent}
      loading={loading}
      onExpressCheckoutReady={onExpressCheckoutReady}
      registrationId={registrationId}
      canMakePayment={canMakePayment}
    />
  )
}

ExpressCheckoutInConfirmationContext.propTypes = {
  stripe: object.isRequired,
  paymentIntent: object.isRequired,
  loading: bool.isRequired,
  onExpressCheckoutReady: func.isRequired,
  registrationId: string.isRequired,
  canMakePayment: bool,
}

const ExpressCheckoutInReservationContext = ({
  stripe,
  paymentIntent,
  loading,
  onExpressCheckoutReady,
  eventId,
  reservationUuid,
  canMakePayment,
}) => {
  const mutation = useReservationMutation(eventId, reservationUuid)

  return (
    <ExpressCheckout
      stripe={stripe}
      paymentIntent={paymentIntent}
      loading={loading}
      canMakePayment={canMakePayment}
      onExpressCheckoutReady={onExpressCheckoutReady}
      mutation={mutation}
    />
  )
}

ExpressCheckoutInReservationContext.propTypes = {
  stripe: object.isRequired,
  paymentIntent: object.isRequired,
  loading: bool.isRequired,
  onExpressCheckoutReady: func.isRequired,
  eventId: string.isRequired,
  reservationUuid: string.isRequired,
  canMakePayment: bool,
}

const ExpressCheckout = ({
  stripe,
  paymentIntent,
  loading,
  onExpressCheckoutReady,
  mutation,
  registrationId,
  canMakePayment,
}) => {
  const navigate = useNavigate()

  const handleExpressCheckoutClick = (event) => event.resolve()

  const handleExpressCheckoutConfirm = async (_event) => {
    const { error, paymentIntent: response } = await stripe.confirmPayment({
      elements,
      clientSecret: paymentIntent.clientSecret,
      redirect: "if_required",
    })

    if (!error) {
      if (mutation) {
        mutation.reset()
        let attributes = {
          method: "creditCard",
          paymentIntentId: response?.id,
          amount: response?.amount,
          status: response?.status,
        }

        const reservation = await mutation.mutateAsync({
          data: {
            attributes: {
              send_confirmation: true,
            },
          },
          included: [{ type: "Payment", attributes }],
        })

        const path = `/registrations/registrations/${reservation.registrationId}/confirmation`
        navigate(path, { state: { showNotification: true } })
      } else {
        const path = `/registrations/registrations/${registrationId}/confirmation`
        navigate(path, { state: { showNotification: true } })
      }
    }
  }

  const elements = useElements()

  const env = useEnv()

  return paymentIntent?.clientSecret && paymentIntent?.amount > 0 ? (
    <CenterLabelBorder label="Quick Pay">
      {(loading || !canMakePayment) && <ExpressCheckoutLoading />}
      {canMakePayment && (
        <ExpressCheckoutElement
          onClick={(e) => {
            handleExpressCheckoutClick(e)
          }}
          onConfirm={(e) => {
            handleExpressCheckoutConfirm(e)
          }}
          onReady={(e) => {
            onExpressCheckoutReady(e)
          }}
          options={{
            paymentMethods: {
              applePay: "auto",
              googlePay: "auto",
              amazonPay: env === "development" ? "auto" : "never",
              link: "never",
              paypal: "never",
              klarna: "never",
            },
          }}
        />
      )}
    </CenterLabelBorder>
  ) : null
}

ExpressCheckout.propTypes = {
  stripe: object,
  loading: bool,
  paymentIntent: object,
  onExpressCheckoutReady: func,
  onPaymentSave: func,
  mutation: object,
  registrationId: string,
  canMakePayment: bool,
}

export default ExpressCheckoutWrapper

const ExpressCheckoutLoading = () => {
  const loadingStyles = {
    flexGrow: 1,
    height: "44px",
    borderRadius: "4px",
    backgroundColor: "var(--color-tint9)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "0.5rem",
    padding: "1rem",
  }

  return (
    <div className="d-f g-2">
      {[1, 2, 3].map((i) => (
        <div key={i} css={loadingStyles}>
          <div
            className="loading-placeholder"
            css={{ height: "1rem", width: "5rem" }}
          ></div>
        </div>
      ))}
    </div>
  )
}
